import { useState, useRef } from "react";
import { FcDepartment, FcBusinessman, FcHome } from "react-icons/fc";
import { BsCurrencyEuro } from "react-icons/bs";
import { HiArrowRight, HiOutlineArrowRightCircle } from "react-icons/hi2";
import useInput from "../../hooks/userInput";
import { useSelector, useDispatch } from "react-redux";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";

import cf from "../UI/ClassicForm.module.css";
import { sendToken } from "../../api/api";
import { transferActions } from "../../store/transfer-slice";
import { alertActions } from "../../store/alert-slice";
import Spinner from "../UI/Spinner";

const Local = () => {
  const sender = useSelector((state) => state.auth.user.accountNumber);
  const [showSpinner, setShowSpinner] = useState(false);
  const dispatch = useDispatch();
  const { jwt } = useCookies(["jwt"])[0];
  const navigate = useNavigate();
  const amountRef = useRef(null);
  const [amount, setAmount] = useState("0.00");
  const {
    value: bankNameInput,
    enteredValueIsValid: bankNameInputIsValid,
    hasError: bankNameInputIsInvalid,
    valueInputChangedHandler: bankNameInputChangedHandler,
    valueInputBlurHandler: bankNameInputBlurHandler,
  } = useInput((value) => value.trim() !== "");

  const {
    value: nameInput,
    enteredValueIsValid: nameInputIsValid,
    hasError: nameInputIsInvalid,
    valueInputChangedHandler: nameInputChangedHandler,
    valueInputBlurHandler: nameInputBlurHandler,
  } = useInput((value) => value.trim() !== "");

  const {
    value: accountInput,
    enteredValueIsValid: accountInputIsValid,
    hasError: accountInputIsInvalid,
    valueInputChangedHandler: accountInputChangedHandler,
    valueInputBlurHandler: accountInputBlurHandler,
  } = useInput((value) => value.trim() !== "");

  const handleAmountChange = (e) => {
    const enteredValue = e.target.value.replace(/[^\d]/g, "");

    const decimalPart = enteredValue.slice(-2);
    const integerPart = enteredValue.slice(0, -2).replace(/^0+/, "");

    const formattedIntegerPart = integerPart.replace(
      /\B(?=(\d{3})+(?!\d))/g,
      ","
    );

    const formattedValue = `${
      formattedIntegerPart.length === 0 ? "0" : formattedIntegerPart
    }.${decimalPart}`;

    setAmount(formattedValue);

    if (amountRef.current) {
      amountRef.current.setSelectionRange(
        formattedValue.length,
        formattedValue.length
      );
    }
  };

  let formIsValid = false;
  if (
    bankNameInputIsValid &&
    nameInputIsValid &&
    accountInputIsValid &&
    amount !== "0.00"
  ) {
    formIsValid = true;
  }

  const submitHandler = async (e) => {
    e.preventDefault();
    setShowSpinner(true);

    const transactionInfo = {
      kind: "Domestic",
      type: "debit",
      bankName: bankNameInput,
      receipientName: nameInput,
      receipient: accountInput,
      amount: amount.replace(/,/g, ""),
      sender,
    };

    const res = await sendToken(jwt);

    if (res.status === "success") {
      dispatch(transferActions.transfer({ transactions: transactionInfo }));
      navigate("/account/confirmLocal");
    } else {
      dispatch(
        alertActions.setState({ message: res.message, status: "error" })
      );
    }

    setShowSpinner(false);
  };

  const bankNameInputClasses = bankNameInputIsInvalid
    ? `${cf.field} ${cf.invalid}`
    : cf.field;
  const nameInputClasses = nameInputIsInvalid
    ? `${cf.field} ${cf.invalid}`
    : cf.field;
  const accountInputClasses = accountInputIsInvalid
    ? `${cf.field} ${cf.invalid}`
    : cf.field;

  return (
    <form className={cf.form} onSubmit={submitHandler}>
      {showSpinner && <Spinner />}
      <div className={cf.formTitleRow}>
        <HiOutlineArrowRightCircle className={cf.formTitleIcon} aria-hidden />
        <h2 className={cf.formTitle}>Domestic transfer</h2>
      </div>
      <p className={cf.formSubtitle}>
        Enter the beneficiary details and amount. We will email you a token to
        confirm.
      </p>
      <div className={bankNameInputClasses}>
        <label htmlFor="local-bank">Beneficiary bank name</label>
        <div className={cf.inputGroup}>
          <FcHome className={cf.icon} />
          <input
            id="local-bank"
            type="text"
            value={bankNameInput}
            onChange={bankNameInputChangedHandler}
            onBlur={bankNameInputBlurHandler}
          />
        </div>
        {bankNameInputIsInvalid && (
          <span className={cf.fieldError}>Please enter beneficiary bank name</span>
        )}
      </div>
      <div className={nameInputClasses}>
        <label htmlFor="local-name">Beneficiary name</label>
        <div className={cf.inputGroup}>
          <FcBusinessman className={cf.icon} />
          <input
            id="local-name"
            type="text"
            value={nameInput}
            onChange={nameInputChangedHandler}
            onBlur={nameInputBlurHandler}
          />
        </div>
        {nameInputIsInvalid && (
          <span className={cf.fieldError}>Please enter beneficiary name</span>
        )}
      </div>
      <div className={accountInputClasses}>
        <label htmlFor="local-account">Account number</label>
        <div className={cf.inputGroup}>
          <FcDepartment className={cf.icon} />
          <input
            id="local-account"
            type="text"
            inputMode="numeric"
            value={accountInput}
            onChange={accountInputChangedHandler}
            onBlur={accountInputBlurHandler}
          />
        </div>
        {accountInputIsInvalid && (
          <span className={cf.fieldError}>
            Please enter beneficiary account number
          </span>
        )}
      </div>
      <div className={cf.field}>
        <label htmlFor="local-amount">Amount</label>
        <div className={cf.inputGroup}>
          <BsCurrencyEuro className={cf.icon} />
          <input
            id="local-amount"
            type="text"
            value={amount}
            onChange={handleAmountChange}
            ref={amountRef}
          />
        </div>
      </div>
      <div className={cf.actions}>
        <button type="submit" disabled={!formIsValid}>
          <HiArrowRight className={cf.btnIcon} aria-hidden />
          Next
        </button>
      </div>
    </form>
  );
};

export default Local;
